import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Mail, Plus, X } from 'lucide-react';
import Modal from '../Modal';
import { ticketsApi } from '../../api/tickets';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_RECIPIENTS = 10;

interface ShareTicketModalProps {
  ticketId: string;
  ticketNumber: string;
  onClose: () => void;
}

export default function ShareTicketModal({ ticketId, ticketNumber, onClose }: ShareTicketModalProps) {
  const queryClient = useQueryClient();
  const [input, setInput] = useState('');
  const [emails, setEmails] = useState<string[]>([]);

  const shareMutation = useMutation({
    mutationFn: (list: string[]) => ticketsApi.share(ticketId, list.map(email => ({ email }))),
    onSuccess: (_, list) => {
      queryClient.invalidateQueries({ queryKey: ['ticket-activity', ticketId] });
      toast.success(`Ticket shared with ${list.length} recipient${list.length === 1 ? '' : 's'}`);
      onClose();
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.message ?? err?.message ?? 'Failed to share ticket'),
  });

  const addEmail = (): boolean => {
    const e = input.trim().toLowerCase();
    if (!e) return true; // nothing pending
    if (!EMAIL_RE.test(e)) {
      toast.error('Enter a valid email address');
      return false;
    }
    if (emails.length >= MAX_RECIPIENTS) {
      toast.error(`Maximum ${MAX_RECIPIENTS} recipients`);
      return false;
    }
    if (!emails.includes(e)) setEmails(prev => [...prev, e]);
    setInput('');
    return true;
  };

  const handleShare = () => {
    // Fold any email still sitting in the input into the list before sending.
    const pending = input.trim().toLowerCase();
    let list = emails;
    if (pending) {
      if (!EMAIL_RE.test(pending)) {
        toast.error('Enter a valid email address');
        return;
      }
      if (!list.includes(pending)) list = [...list, pending];
    }
    if (list.length === 0) {
      toast.error('Add at least one recipient');
      return;
    }
    if (list.length > MAX_RECIPIENTS) {
      toast.error(`Maximum ${MAX_RECIPIENTS} recipients`);
      return;
    }
    shareMutation.mutate(list);
  };

  return (
    <Modal
      open
      onClose={onClose}
      busy={shareMutation.isPending}
      title={
        <span className="flex items-center gap-2">
          <Mail className="w-4 h-4" /> Share ticket {ticketNumber}
        </span>
      }
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={shareMutation.isPending}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleShare}
            disabled={shareMutation.isPending || (emails.length === 0 && !input.trim())}
          >
            {shareMutation.isPending ? 'Sending…' : 'Share'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-muted">
          Each recipient receives an email with a secure, expiring link to view this
          ticket. The email itself contains no ticket content.
        </p>

        <div>
          <label className="label" htmlFor="share-email-input">Recipient email</label>
          <div className="flex gap-2">
            <input
              id="share-email-input"
              type="email"
              className="input flex-1"
              placeholder="name@example.com"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addEmail();
                }
              }}
              autoFocus
            />
            <button type="button" className="btn btn-secondary" onClick={addEmail} disabled={!input.trim()}>
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
          </div>
        </div>

        {emails.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {emails.map(e => (
              <span key={e} className="badge flex items-center gap-1">
                {e}
                <button
                  type="button"
                  onClick={() => setEmails(prev => prev.filter(x => x !== e))}
                  aria-label={`Remove ${e}`}
                  className="text-muted hover:text-fg"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 0 }}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
